// Great Lakes 3D viewer: NCEI lakebed for all five lakes, plus each lake's
// full NOAA temperature and current volume shown as a depth slice, a target
// temperature surface, a cross-section, and flow particles.
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { convertUnitValue, formatUnitValue, unitPreference } from "./units.js";
import { BED_PALETTES, TEMPERATURE_STOPS } from "./palette-stops.js";
import { setupAnalytics } from "./analytics.js";
import { createThreeDAnalytics, depthBucket, exaggerationBucket, targetBucket } from "./three-d/analytics.js";
import { LAKES, LAKE_IDS, LAKE_MAP_VIEWS, LAKE_NAMES, distanceKm, fromWorld, toWorld } from "./three-d/geo.js";
import { MIN_ORBIT_KM, cameraFloor, floorHeight, pivotDistance, terrainDistance } from "./three-d/camera.js";
import {
  bedDepthAt, buildBedField, currentAt, currentsAtDepth, fetchBinary, fetchJson, insideGrid, lakeCrossing, lakeIdAt, loadTerrain,
  parseVolume, temperatureAt, temperatureProfile
} from "./three-d/data.js";
import {
  landMaterial, reliefUniforms, sharedUniforms, terrainMaterial, waterSurfaceMaterial
} from "./three-d/materials.js";
import {
  PROBE_OPACITY, WaterLayer, bedTexture, groundPlane, haloTexture, isothermTexture, paletteColor,
  paletteTexture, pinMarker, probe, sectionFloorLine, terrainMesh
} from "./three-d/layers.js";
import { VolumeLoader, lakeLoadOrder, temperatureBounds, temperatureRange } from "./three-d/water.js";
import { drawProfile, drawSection } from "./three-d/charts.js";
import { floatingCard } from "./three-d/floating-card.js";
import { controlsPanel } from "./three-d/panel.js";
import "../css/three-d.css";

const FORECASTS = [0, 6, 12, 24, 48];
const LAYERS = ["slice", "currents", "target", "section"];
const DEFAULT_LAYERS = new Set(["slice", "currents"]);
const BED_PALETTE_NAMES = Object.keys(BED_PALETTES);
const PARTICLE_DENSITIES = [1, 1.5, 2];
const PARTICLE_COLORS = ["speed", "cyan", "blue", "violet", "green", "white", "black"];
const MIN_EXAGGERATION = 0;
const DEFAULT_EXAGGERATION = 100;
const MAX_EXAGGERATION = 300;
const DEFAULT_TARGET_C = 10;
const DEPTH_SLIDER_STEPS = 1000;
// Above water the camera stays this far from level; under water it may look in any direction, up at the surface included.
const ABOVE_WATER_POLAR = Math.PI * 0.47;
const UNDERWATER_POLAR = Math.PI * 0.999;
const ORBIT_KEYS = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "+", "=", "-", "_"]);
const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");

const root = document.querySelector("[data-three-d]");
if (root) {
  setupAnalytics();
  void startViewer();
}

async function startViewer() {
  const $ = (selector) => root.querySelector(selector);
  const ui = {
    canvas: $("[data-scene]"),
    stage: $("[data-stage]"),
    overlay: $("[data-overlay]"),
    panel: $("[data-panel]"),
    panelHeader: $("[data-panel-header]"),
    panelToggle: $("[data-panel-toggle]"),
    status: $("[data-status]"),
    forecast: $("[data-forecast]"),
    depth: $("[data-depth]"),
    depthLabel: $("[data-depth-label]"),
    layerToggles: Object.fromEntries([...root.querySelectorAll("[data-layer]")].map((input) => [input.dataset.layer, input])),
    currentControls: $("[data-current-controls]"),
    particleDensity: $("[data-particle-density]"),
    particleColor: $("[data-particle-color]"),
    targetControls: $("[data-target-controls]"),
    target: $("[data-target]"),
    targetLabel: $("[data-target-label]"),
    targetStatus: $("[data-target-status]"),
    sectionControls: $("[data-section-controls]"),
    sectionCard: $("[data-section-card]"),
    sectionHandle: $("[data-section-handle]"),
    sectionGrip: $("[data-section-grip]"),
    legendSlot: $("[data-legend-slot]"),
    dock: $("[data-dock]"),
    sectionClose: $("[data-section-close]"),
    sectionDynamicScale: $("[data-section-dynamic-scale]"),
    fitMapRange: $("[data-fit-map-range]"),
    sectionChart: $("[data-section-chart]"),
    sectionCaption: $("[data-section-caption]"),
    dataStatus: $("[data-data-status]"),
    exaggeration: $("[data-exaggeration]"),
    exaggerationLabel: $("[data-exaggeration-label]"),
    contours: $("[data-contours]"),
    bedPalette: $("[data-bed-palette]"),
    readout: $("[data-readout]"),
    readoutTitle: $("[data-readout-title]"),
    readoutValues: $("[data-readout-values]"),
    readoutNote: $("[data-readout-note]"),
    readoutProfile: $("[data-readout-profile]"),
    legend: $("[data-legend]"),
    legendBar: $("[data-legend-bar]"),
    legendTicks: $("[data-legend-ticks]"),
    legendTemperature: $("[data-legend-temperature]"),
    legendTarget: $("[data-legend-target]"),
    legendTargetTitle: $("[data-legend-target-title]"),
    legendTargetBar: $("[data-legend-target-bar]"),
    legendTargetTicks: $("[data-legend-target-ticks]"),
    pickHint: $("[data-pick-hint]"),
    cameraDepth: $("[data-camera-depth]"),
    fallback: $("[data-webgl-fallback]"),
    returnMap: $("[data-return-map]"),
    share: $("[data-share-view]"),
    shareFeedback: $("[data-share-feedback]"),
    reset: $("[data-reset-camera]")
  };

  // Reports to the site's anonymous analytics; `state` is only read once a visitor acts.
  const stats = createThreeDAnalytics({ lake: () => (state.lake ? LAKE_NAMES[state.lake] : "All lakes") });
  window.addEventListener("pagehide", stats.flush);
  const panel = controlsPanel({
    root, stage: ui.stage, panel: ui.panel, toggle: ui.panelToggle, header: ui.panelHeader,
    onToggle: (open) => stats.interaction("panel_toggled", open ? "shown" : "hidden")
  });
  // Both cards float over the part of the stage the panel leaves free and can be dragged anywhere in it.
  const sectionCard = floatingCard({ stage: ui.overlay, card: ui.sectionCard, handle: ui.sectionHandle, grip: ui.sectionGrip, ignore: "[data-section-close]" });
  let latestReadoutProfile = null;
  const readoutCard = floatingCard({
    stage: ui.overlay,
    card: ui.readout,
    resizable: true,
    onResize: () => {
      if (!ui.readout.hidden && latestReadoutProfile) drawProfile(ui.readoutProfile, latestReadoutProfile);
    }
  });

  const state = readUrlState();
  let terrain = [];
  let field = null;
  const waterLayers = new Map();
  let displayRange = [0, 30];
  let visibleSliceRange = null;
  let visibleSliceRangeTimer = null;
  let particleViewTimer = null;
  let targetWaterBounds = null;
  let sectionColorRange = null;
  let isothermInfo = null;
  let needsRender = true;
  let flight = null;
  let pick = null;
  let pointer = null;
  let pointerDown = null;
  let hoverScheduled = false;
  let urlTimer = null;
  let lastFrame = performance.now();
  let chartWidth = 0;
  // The canvas size, and how far the scene's centre is moved off it (half the panel's insets),
  // easing toward the panel's current insets after it opens or closes.
  let viewSize = { width: 0, height: 0 };
  const viewShift = { ...panel.insets };
  // How far under the surface the camera is (0 above water, 1 well below), and what the page last showed of it.
  let dive = 0;
  let shownDive = "";
  let shownDepth = "";
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  const hit = new THREE.Vector3();
  const fitFrustum = new THREE.Frustum();
  const fitViewProjection = new THREE.Matrix4();
  const fitPoint = new THREE.Vector3();
  const forward = new THREE.Vector3();
  const lookOffset = new THREE.Vector3();

  // The canvas is transparent so the page's backdrop gradient shows around the map.
  const context = ui.canvas.getContext("webgl2", { antialias: true, alpha: true, powerPreference: "high-performance" });
  if (!context) return showFallback();
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas: ui.canvas, context, antialias: true, alpha: true });
  } catch {
    return showFallback();
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setClearColor(0x000000, 0);
  renderer.localClippingEnabled = true;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(40, 1, 0.5, 20000);
  const depthGroup = new THREE.Group();
  depthGroup.scale.y = state.exaggeration ?? DEFAULT_EXAGGERATION;
  scene.add(depthGroup);
  const markers = new THREE.Group();
  scene.add(markers);

  const orbit = new OrbitControls(camera, ui.canvas);
  orbit.enableDamping = true;
  orbit.dampingFactor = 0.08;
  orbit.screenSpacePanning = false;
  orbit.zoomToCursor = true;
  orbit.maxPolarAngle = ABOVE_WATER_POLAR;
  orbit.minDistance = MIN_ORBIT_KM;
  orbit.maxDistance = 4000;
  // Middle-drag pans like a map (OrbitControls would dolly); the wheel still zooms.
  orbit.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
  orbit.addEventListener("change", () => {
    requestRender();
    scheduleUrl();
    scheduleParticleViewUpdate();
    if (state.fitMapRange) scheduleVisibleSliceRangeFit();
  });
  orbit.addEventListener("start", () => { flight = null; placePivot(); });

  const paletteTex = paletteTexture(TEMPERATURE_STOPS);
  const bedPaletteTextures = Object.fromEntries(Object.entries(BED_PALETTES).map(([name, stops]) => [name, paletteTexture(stops)]));
  let shared;
  let relief;
  let materials;
  const meshes = { terrain: new Map(), land: null, water: null, floor: null };
  const particleCut = new THREE.Plane();
  const volumeLoader = new VolumeLoader({
    load: async (lake, forecast, hour, signal) => {
      const query = new URLSearchParams({ lake, forecastHour: String(forecast), data: hour });
      const startedAt = performance.now();
      try {
        const volume = parseVolume(await fetchBinary(`/api/v1/3d/volume?${query}`, { signal }));
        stats.request("/3d/volume", startedAt);
        return volume;
      } catch (error) {
        // A download dropped because the visitor moved on is not a failure.
        if (!signal.aborted) {
          stats.request("/3d/volume", startedAt, error);
          stats.issue("volume_failed", LAKE_NAMES[lake]);
        }
        throw error;
      }
    },
    order: loadingOrder,
    onLoad: showVolume,
    onChange: updateLoadingStatus
  });
  const probeLine = probe();
  depthGroup.add(probeLine);
  const pins = [pinMarker(0xffffff), pinMarker(0xffdf92)];
  pins.forEach((pin) => { pin.visible = false; markers.add(pin); });

  bindControls();
  syncControls();
  resize();
  new ResizeObserver(resize).observe(ui.stage);
  new ResizeObserver(placeCards).observe(ui.overlay);
  new ResizeObserver(() => {
    readoutCard.place();
    if (!ui.readout.hidden && latestReadoutProfile) drawProfile(ui.readoutProfile, latestReadoutProfile);
  }).observe(ui.readout);
  requestAnimationFrame(frame);

  try {
    setStatus("Loading lakebed…", "loading");
    const manifest = await fetchJson("/static/data/bathymetry/manifest.json", { cache: "no-cache" });
    terrain = await loadTerrain(manifest);
    field = buildBedField(terrain);
    setExaggeration(state.exaggeration ?? DEFAULT_EXAGGERATION);
    buildBaseScene();
    setStatus("Lakebed ready", "ready");
    applyLake(true);
    stats.ready("lakebed");
  } catch (error) {
    stats.issue("lakebed_failed");
    setStatus("Lakebed unavailable", "error");
    showDataStatus(error instanceof Error ? error.message : "The lakebed grids could not be loaded. The 2D map is still available.");
  }

  // ---------- Scene construction ----------

  function buildBaseScene() {
    shared = sharedUniforms(bedTexture(field), field);
    relief = reliefUniforms();
    relief.uDepthPalette.value = bedPaletteTextures[state.bedPalette];
    materials = {
      land: landMaterial(shared, haloTexture(field)),
      water: waterSurfaceMaterial(shared)
    };
    for (const lake of terrain) {
      const mesh = terrainMesh(lake, terrainMaterial(shared, relief, LAKE_IDS[lake.slug]));
      meshes.terrain.set(lake.slug, mesh);
      depthGroup.add(mesh);
    }
    const plane = groundPlane(field.south, field.west, field.north, field.east);
    meshes.land = new THREE.Mesh(plane, materials.land);
    meshes.water = new THREE.Mesh(plane, materials.water);
    meshes.water.renderOrder = 5;
    scene.add(meshes.land, meshes.water);
    updateSceneOptions();
  }

  function updateSceneOptions() {
    if (!materials) return;
    relief.uDepthPalette.value = bedPaletteTextures[state.bedPalette];
    relief.uContours.value = state.contours ? 1 : 0;
    relief.uContourInterval.value = contourInterval();
    // A lake in the URL chooses the starting view; every lake stays in context.
    relief.uFocus.value = 0;
    relief.uExaggeration.value = state.exaggeration;
    shared.uLakeId.value = 0;
    const isothermStep = unitPreference("waterTemperature") === "F" ? 2 * 5 / 9 : 1;
    for (const layer of waterLayers.values()) {
      for (const key of ["slice", "curtain"]) {
        layer.materials[key].uniforms.uIsothermStep.value = isothermStep;
        layer.materials[key].uniforms.uTarget.value = state.targetC;
        layer.materials[key].uniforms.uShowTarget.value = state.layers.has("target") ? 1 : 0;
      }
      layer.materials.curtain.uniforms.uGridStep.value = contourInterval();
      layer.materials.isotherm.uniforms.uContourInterval.value = contourInterval() / 2;
    }
    requestRender();
  }

  function contourInterval() {
    const unit = unitPreference("depth");
    const maximum = 400;
    const display = convertUnitValue(maximum, "m", unit);
    const candidates = unit === "m" ? [2, 5, 10, 20, 50] : unit === "fm" ? [1, 2, 5, 10, 20, 50] : [5, 10, 20, 50, 100, 200];
    const step = candidates.find((value) => display / value <= 14) || candidates[candidates.length - 1];
    return convertUnitValue(step, unit, "m");
  }

  // ---------- Lake selection and camera ----------

  function selectLake(slug, source) {
    const next = LAKES.includes(slug) ? slug : null;
    if (next === state.lake) return;
    state.lake = next;
    stats.interaction("lake_selected", next ? LAKE_NAMES[next] : "All lakes", source);
    flyTo(cameraViewFor(next), true);
    syncUrl();
  }

  function setExaggeration(value) {
    const numericValue = Number(value);
    state.exaggeration = clamp(Number.isFinite(numericValue) ? Math.round(numericValue) : DEFAULT_EXAGGERATION, MIN_EXAGGERATION, MAX_EXAGGERATION);
    depthGroup.scale.y = state.exaggeration;
    if (relief) relief.uExaggeration.value = state.exaggeration;
    ui.exaggeration.value = String(state.exaggeration / DEFAULT_EXAGGERATION);
    ui.exaggerationLabel.textContent = exaggerationScaleLabel(state.exaggeration);
    ui.exaggeration.setAttribute("aria-valuetext", exaggerationScaleLabel(state.exaggeration));
    requestRender();
  }

  function applyLake(initial) {
    updateDepthRange();
    if (initial && state.camera) applySavedCamera();
    else flyTo(cameraViewFor(state.lake), !initial);
    if (state.section && !state.sectionDrawn) {
      if (LAKES.some((slug) => sameSection(state.section, defaultSection(slug)))) {
        state.section = null;
      }
    }
    syncControls();
    updateSceneOptions();
    if (!(initial && state.camera) && state.layers.has("section") && state.section) {
      flyTo(sectionView(), !initial);
    }
    void loadVolumes();
    syncUrl();
  }

  // How much of the canvas's width and height the scene has to itself beside or above the panel.
  // Views are fitted into that part, which the projection centres on (see stepViewOffset).
  function freeShare() {
    const { width, height } = viewSize;
    return {
      width: width ? Math.max(0.5, 1 - panel.insets.left / width) : 1,
      height: height ? Math.max(0.5, 1 - panel.insets.bottom / height) : 1
    };
  }

  function cameraViewFor(slug) {
    const lakes = slug ? terrain.filter((lake) => lake.slug === slug) : terrain;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const lake of lakes) {
      const [[south, west], [north, east]] = lake.bounds;
      const [x0, z0] = toWorld(north, west);
      const [x1, z1] = toWorld(south, east);
      minX = Math.min(minX, x0); maxX = Math.max(maxX, x1);
      minZ = Math.min(minZ, z0); maxZ = Math.max(maxZ, z1);
    }
    const width = Math.max(40, maxX - minX);
    const depth = Math.max(40, maxZ - minZ);
    const target = new THREE.Vector3((minX + maxX) / 2, 0, (minZ + maxZ) / 2 + depth * 0.04);
    const vertical = THREE.MathUtils.degToRad(camera.fov);
    const free = freeShare();
    const fitHeight = depth / (2 * Math.tan(vertical / 2) * free.height);
    const fitWidth = width / (2 * Math.tan(vertical / 2) * camera.aspect * free.width);
    const distance = Math.max(fitHeight * 1.05, fitWidth) * (slug ? 1.18 : 1.08);
    const direction = slug ? new THREE.Vector3(0, 0.66, 0.75).normalize() : new THREE.Vector3(0, 0.78, 0.63).normalize();
    return { position: target.clone().addScaledVector(direction, distance), target, distance };
  }

  function flyTo(view, animate, limits = cameraViewFor(null)) {
    setOrbitLimits(limits);
    if (!animate || REDUCED_MOTION.matches) {
      flight = null;
      camera.position.copy(view.position);
      orbit.target.copy(view.target);
      orbit.update();
      requestRender();
      return;
    }
    flight = {
      start: performance.now(),
      duration: 900,
      fromPosition: camera.position.clone(),
      fromTarget: orbit.target.clone(),
      toPosition: view.position,
      toTarget: view.target
    };
  }

  function setOrbitLimits(view) {
    orbit.maxDistance = Math.max(600, view.distance * 4);
  }

  // A low camera facing the cross-section from whichever side it is already seen from.
  function sectionView() {
    const lakes = crossedLakes(...state.section);
    const [x0, z0] = toWorld(...state.section[0]);
    const [x1, z1] = toWorld(...state.section[1]);
    const wall = (Math.max(1, ...lakes.map((slug) => terrain.find((item) => item.slug === slug).maxDepth)) / 1000) * state.exaggeration;
    const centre = new THREE.Vector3((x0 + x1) / 2, -wall * 0.35, (z0 + z1) / 2);
    const normal = new THREE.Vector3(-(z1 - z0), 0, x1 - x0).normalize();
    // Keep the side the camera is on; from the default camera, face south (or east for a north-south line).
    const side = normal.dot(camera.position.clone().sub(centre));
    const preferred = Math.abs(normal.z) > 0.2 ? normal.z : normal.x;
    if (Math.abs(side) > 5 ? side < 0 : preferred < 0) normal.negate();
    const pitch = THREE.MathUtils.degToRad(22);
    const half = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    const free = freeShare();
    const distance = Math.max(Math.hypot(x1 - x0, z1 - z0) / (2 * half * camera.aspect * free.width), (wall * 1.6) / (2 * half * free.height)) * 1.12;
    const direction = normal.multiplyScalar(Math.cos(pitch)).setY(Math.sin(pitch));
    return { position: centre.clone().addScaledVector(direction, distance), target: centre, distance };
  }

  function focusSection() {
    if (!state.section) return;
    flyTo(sectionView(), true);
  }

  function stepFlight(now) {
    if (!flight) return false;
    const t = Math.min(1, (now - flight.start) / flight.duration);
    const eased = t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
    camera.position.lerpVectors(flight.fromPosition, flight.toPosition, eased);
    orbit.target.lerpVectors(flight.fromTarget, flight.toTarget, eased);
    if (t >= 1) {
      flight = null;
      if (pointer) scheduleHover();
    }
    if (state.fitMapRange) scheduleVisibleSliceRangeFit();
    return true;
  }

  function applySavedCamera() {
    setOrbitLimits(cameraViewFor(null));
    camera.position.fromArray(state.camera);
    orbit.target.fromArray(state.look || cameraViewFor(state.lake).target.toArray());
    updatePolarLimit();
    orbit.update();
    requestRender();
  }

  // ---------- Diving ----------

  // The orbit pivot follows what the camera is looking at, so rotating, panning and
  // zooming work about the lakebed, land or cross-section in view rather than a flat
  // plane. It slides along the view direction, which leaves the picture unchanged.
  function placePivot() {
    if (!terrain.length) return;
    camera.getWorldDirection(forward);
    const distance = pivotDistance(terrain, camera.position, forward, {
      exaggeration: state.exaggeration,
      wall: sectionWall(),
      minDistance: orbit.minDistance,
      maxDistance: orbit.maxDistance
    });
    if (distance !== null) orbit.target.copy(camera.position).addScaledVector(forward, distance);
  }

  // The cross-section curtain as a line in scene coordinates, while it is showing.
  function sectionWall() {
    if (!hasSection() || !state.section) return null;
    const [x0, z0] = toWorld(...state.section[0]);
    const [x1, z1] = toWorld(...state.section[1]);
    return { x0, z0, x1, z1 };
  }

  // Under water the camera may look anywhere. Above it the limit is the usual one, unless the
  // camera has surfaced tilted further than that: it keeps that tilt (and gives it up as it tilts
  // back down), so crossing the surface never snaps the view.
  function updatePolarLimit() {
    if (camera.position.y < 0) {
      orbit.maxPolarAngle = UNDERWATER_POLAR;
      return;
    }
    lookOffset.copy(camera.position).sub(orbit.target);
    const tilt = Math.acos(clamp(lookOffset.y / (lookOffset.length() || 1), -1, 1));
    orbit.maxPolarAngle = clamp(tilt, ABOVE_WATER_POLAR, UNDERWATER_POLAR);
  }

  // The camera never sinks into the lakebed or the ground: it is lifted, and the pivot with it.
  function keepAboveFloor() {
    if (!terrain.length) return false;
    const lift = cameraFloor(terrain, camera.position.x, camera.position.z, state.exaggeration) - camera.position.y;
    if (lift <= 0) return false;
    camera.position.y += lift;
    orbit.target.y += lift;
    return true;
  }

  // Sets the haze, the backdrop and the depth chip for how deep the camera is, and returns
  // its height above whatever is below it.
  function updateUnderwater() {
    if (!shared) return Infinity;
    const { x, y, z } = camera.position;
    const height = y >= 0 ? y : y - floorHeight(terrain, x, z, state.exaggeration);
    dive = THREE.MathUtils.smoothstep(-y, -0.05, 0.35);
    shared.uWater.value.set(dive, 1.1 / clamp(height * 1.2, 10, 150));
    const level = dive.toFixed(2);
    if (level !== shownDive) {
      shownDive = level;
      ui.stage.style.setProperty("--underwater", level);
      // The hover marker spans the whole water column, which would cut right across a view from inside it.
      probeLine.material.opacity = PROBE_OPACITY * (1 - 0.6 * dive);
    }
    const depth = y < -0.1 ? `Underwater · ${formatDepth((-y / state.exaggeration) * 1000)} below the surface` : "";
    if (depth !== shownDepth) {
      shownDepth = depth;
      ui.cameraDepth.hidden = !depth;
      ui.cameraDepth.textContent = depth;
      // The chip takes the strip the controls hint sits in, and someone this deep has found the controls.
      if (depth) ui.stage.classList.add("is-engaged");
    }
    stats.underwater(Boolean(depth));
    return height;
  }

  // ---------- Water volume ----------

  function loadingOrder() {
    camera.getWorldDirection(forward);
    const distance = camera.position.y >= 0 && forward.y < -1e-6
      ? -camera.position.y / forward.y
      : terrainDistance(terrain, camera.position, forward, { exaggeration: state.exaggeration, limit: orbit.maxDistance });
    const focus = distance === null ? orbit.target : hit.copy(camera.position).addScaledVector(forward, distance);
    return lakeLoadOrder(terrain, field, fromWorld(focus.x, focus.z));
  }

  async function loadVolumes() {
    if (!field) return;
    volumeLoader.cancel();
    clearWaterLayers();
    hideDataStatus();
    updateWaterLayers();
    try {
      await volumeLoader.start(LAKES, state.forecast);
    } catch (error) {
      volumeLoader.cancel();
      setStatus("Water layers unavailable", "error");
      showDataStatus(error instanceof Error ? error.message : "The water layers could not be drawn. The lakebeds are still shown.");
    }
  }

  function showVolume(slug, volume) {
    const layer = new WaterLayer(volume, shared, relief, paletteTex, particleCountForView());
    waterLayers.set(slug, layer);
    targetWaterBounds = temperatureBounds([...waterLayers.values()].map((item) => item.volume));
    updateParticlePixelRatio();
    depthGroup.add(layer.group);
    stats.ready("water");
    displayRange = temperatureRange([...waterLayers.values()].map((item) => item.volume));
    applyMapTemperatureRange();
    updateWaterLayers();
    if (pointer) scheduleHover();
  }

  function updateLoadingStatus() {
    const targetChanged = syncTargetSlider();
    if (targetChanged) updateWaterLayers();
    const total = LAKES.length;
    const time = state.forecast ? `${state.forecast} h forecast` : "now";
    if (volumeLoader.loading) {
      setStatus(`Loading ${LAKE_NAMES[volumeLoader.loading]} · ${waterLayers.size}/${total} ready…`, "loading");
    } else if (volumeLoader.pending.size) {
      setStatus(`${waterLayers.size}/${total} lakes ready`, "loading");
    } else if (volumeLoader.failed.size) {
      setStatus(waterLayers.size ? `${waterLayers.size}/${total} lakes ready · ${time}` : "Water data unavailable", "error");
    } else {
      setStatus(`All five lakes · ${time}`, "ready");
    }
    const warnings = [];
    if (volumeLoader.failed.size) warnings.push(`Water data unavailable for ${[...volumeLoader.failed.keys()].map((slug) => LAKE_NAMES[slug]).join(", ")}. Lakebeds remain visible. Change forecast to retry.`);
    const missingCurrents = [...waterLayers.values()].filter((layer) => !layer.volume.currents);
    if (missingCurrents.length) warnings.push(`Currents unavailable for ${missingCurrents.map((layer) => LAKE_NAMES[layer.volume.lake]).join(", ")}; temperature is shown.`);
    if (warnings.length) showDataStatus(warnings.join(" "));
    else hideDataStatus();
    if (pointer) scheduleHover();
  }

  function clearWaterLayers() {
    for (const layer of waterLayers.values()) {
      depthGroup.remove(layer.group);
      layer.dispose();
    }
    waterLayers.clear();
    displayRange = [0, 30];
    visibleSliceRange = null;
    clearTimeout(visibleSliceRangeTimer);
    visibleSliceRangeTimer = null;
    targetWaterBounds = null;
    syncTargetSlider();
    sectionColorRange = null;
    isothermInfo = null;
    if (meshes.floor) {
      depthGroup.remove(meshes.floor);
      meshes.floor.geometry.dispose();
      meshes.floor.material.dispose();
      meshes.floor = null;
    }
    if (shared) {
      shared.uCut.value.w = 0;
      shared.uCutLakes.value = 0;
    }
    pins.forEach((pin) => { pin.visible = false; });
    ui.legend.hidden = true;
    ui.sectionCard.hidden = true;
    placeLegend();
    hideReadout();
    pick = null;
    cancelPickUi();
    requestRender();
  }

  function updateWaterLayers() {
    const depth = state.depth;
    syncFitMapRangeControl();
    ui.currentControls.hidden = !state.layers.has("currents");
    for (const [slug, layer] of waterLayers) {
      layer.particles.setCount(particleCountForView());
      layer.particles.setPalette(state.particleColor);
      layer.slice.visible = state.layers.has("slice");
      layer.slice.position.y = -depth / 1000;
      layer.materials.slice.uniforms.uSliceDepth.value = depth;
      const showCurrents = state.layers.has("currents") && Boolean(layer.volume.currents);
      if (showCurrents && (!layer.currentField || layer.currentField.depth !== depth)) {
        layer.currentField = currentsAtDepth(layer.volume, depth);
        layer.particles.setField(layer.currentField, field, depth, LAKE_IDS[slug]);
      }
      layer.particles.object.visible = showCurrents;
      layer.particles.dots.visible = showCurrents;
    }
    updateIsotherm();
    updateSection();
    updateSceneOptions();
    updateLegend();
    requestRender();
    if (state.fitMapRange && state.layers.has("slice")) scheduleVisibleSliceRangeFit();
    scheduleParticleViewUpdate();
  }

  function normalMapTemperatureRange() {
    return state.fitMapRange && state.layers.has("slice") && visibleSliceRange ? visibleSliceRange : displayRange;
  }

  function applyMapTemperatureRange() {
    const range = normalMapTemperatureRange();
    for (const layer of waterLayers.values()) layer.uniforms.uRange.value.set(...range);
    updateIsotherm();
    updateLegend();
    requestRender();
  }

  function scheduleVisibleSliceRangeFit() {
    clearTimeout(visibleSliceRangeTimer);
    visibleSliceRangeTimer = setTimeout(() => {
      visibleSliceRangeTimer = null;
      fitVisibleSliceRange();
    }, 180);
  }

  function fitVisibleSliceRange() {
    if (!state.fitMapRange || !state.layers.has("slice") || !field || !waterLayers.size || !viewSize.width || !viewSize.height) return;

    camera.updateMatrixWorld(true);
    fitViewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    fitFrustum.setFromProjectionMatrix(fitViewProjection);
    const stageBounds = ui.stage.getBoundingClientRect();
    const hiddenBy = [ui.panel, ui.readout, ui.sectionCard, ui.legend, ui.pickHint, ui.reset]
      .filter((element) => element && !element.hidden && getComputedStyle(element).display !== "none")
      .map((element) => {
        const bounds = element.getBoundingClientRect();
        return {
          left: bounds.left - stageBounds.left,
          right: bounds.right - stageBounds.left,
          top: bounds.top - stageBounds.top,
          bottom: bounds.bottom - stageBounds.top
        };
      });
    const totalCells = [...waterLayers.values()].reduce((total, layer) => (
      total + layer.volume.temperature.rows * layer.volume.temperature.columns
    ), 0);
    const stride = Math.max(1, Math.ceil(Math.sqrt(totalCells / 24000)));
    const worldY = -(state.depth / 1000) * state.exaggeration;
    const cut = shared?.uCut.value;
    const cutLakes = shared?.uCutLakes.value || 0;
    const values = [];

    for (const [slug, layer] of waterLayers) {
      if (!layer.slice.visible) continue;
      const lakeId = LAKE_IDS[slug];
      const grid = layer.volume.temperature;
      for (let row = 0; row < grid.rows; row += stride) {
        const latitude = grid.south + row * grid.cellLatitude;
        for (let column = 0; column < grid.columns; column += stride) {
          const longitude = grid.west + column * grid.cellLongitude;
          const [x, z] = toWorld(latitude, longitude);
          fitPoint.set(x, worldY, z);
          if (!fitFrustum.containsPoint(fitPoint)) continue;
          fitPoint.project(camera);
          const screenX = ((fitPoint.x + 1) / 2) * viewSize.width;
          const screenY = ((1 - fitPoint.y) / 2) * viewSize.height;
          if (screenX < 0 || screenX > viewSize.width || screenY < 0 || screenY > viewSize.height) continue;
          if (hiddenBy.some((bounds) => screenX >= bounds.left && screenX <= bounds.right && screenY >= bounds.top && screenY <= bounds.bottom)) continue;
          if (lakeIdAt(field, latitude, longitude) !== lakeId || bedDepthAt(field, latitude, longitude) <= state.depth + 0.5) continue;
          if (cut?.w > 0.5 && (cutLakes & (1 << lakeId)) !== 0) {
            const cutSide = ((x - cut.x) * -cut.z + (z - cut.y)) * Math.sign(cut.w - 1.5);
            if (cutSide > 0) continue;
          }
          const temperature = temperatureAt(layer.volume, latitude, longitude, state.depth);
          if (Number.isFinite(temperature)) values.push(temperature);
        }
      }
    }

    if (values.length) {
      values.sort((first, second) => first - second);
      let low = values[Math.floor((values.length - 1) * 0.005)];
      let high = values[Math.floor((values.length - 1) * 0.995)];
      if (high - low < 3) {
        const middle = (low + high) / 2;
        low = middle - 1.5;
        high = middle + 1.5;
      }
      visibleSliceRange = [low, high];
    } else visibleSliceRange = null;

    applyMapTemperatureRange();
  }

  function particleCountForView() {
    return Math.round(500 * state.particleDensity);
  }

  function scheduleParticleViewUpdate() {
    if (!waterLayers.size || !state.layers.has("currents")) return;
    clearTimeout(particleViewTimer);
    particleViewTimer = setTimeout(() => {
      particleViewTimer = null;
      const bounds = visibleParticleBounds();
      for (const layer of waterLayers.values()) layer.particles.setSpawnBounds(bounds);
    }, 240);
  }

  function visibleParticleBounds() {
    if (!viewSize.width || !viewSize.height) return null;
    camera.updateMatrixWorld(true);
    const planeY = -(state.depth / 1000) * state.exaggeration;
    const corners = [];
    for (const [x, y] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
      const point = new THREE.Vector3(x, y, 0.5).unproject(camera);
      const direction = point.sub(camera.position).normalize();
      if (Math.abs(direction.y) < 1e-5) continue;
      const distance = (planeY - camera.position.y) / direction.y;
      if (!Number.isFinite(distance) || distance <= 0 || distance > camera.far) continue;
      corners.push(fromWorld(
        camera.position.x + direction.x * distance,
        camera.position.z + direction.z * distance
      ));
    }
    if (corners.length < 2) {
      const [latitude, longitude] = fromWorld(orbit.target.x, orbit.target.z);
      const distance = Math.max(1, camera.position.distanceTo(orbit.target));
      const halfHeight = distance * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
      const halfWidth = halfHeight * Math.max(1, camera.aspect);
      const longitudeScale = 111.32 * Math.max(0.2, Math.cos(latitude * Math.PI / 180));
      const latitudeRadius = halfHeight / 111.32;
      const longitudeRadius = halfWidth / longitudeScale;
      return {
        south: latitude - latitudeRadius,
        north: latitude + latitudeRadius,
        west: longitude - longitudeRadius,
        east: longitude + longitudeRadius
      };
    }
    // Keep the look point in the sample area so steep, close views still seed
    // the water directly below the centre of the screen.
    corners.push(fromWorld(orbit.target.x, orbit.target.z));
    const latitudes = corners.map(([latitude]) => latitude);
    const longitudes = corners.map(([, longitude]) => longitude);
    return {
      south: Math.min(...latitudes),
      north: Math.max(...latitudes),
      west: Math.min(...longitudes),
      east: Math.max(...longitudes)
    };
  }

  function updateIsotherm() {
    const show = waterLayers.size > 0 && state.layers.has("target");
    ui.targetControls.hidden = !state.layers.has("target");
    isothermInfo = null;
    if (!show) {
      for (const layer of waterLayers.values()) layer.isotherm.visible = false;
      ui.targetStatus.textContent = "Waiting for water data…";
      return;
    }
    const found = [];
    const colorRange = normalMapTemperatureRange();
    const [r, g, b] = paletteColor((state.targetC - colorRange[0]) / (colorRange[1] - colorRange[0] || 1), TEMPERATURE_STOPS);
    for (const layer of waterLayers.values()) {
      const uniforms = layer.materials.isotherm.uniforms;
      if (!layer.isothermInfo || layer.targetC !== state.targetC) {
        const previous = uniforms.uSurface.value;
        layer.isothermInfo = isothermTexture(layer.volume, state.targetC, previous);
        layer.targetC = state.targetC;
        uniforms.uSurface.value = layer.isothermInfo.texture;
        if (previous && previous !== layer.isothermInfo.texture) previous.dispose();
      }
      uniforms.uTargetColor.value.setRGB(r / 255, g / 255, b / 255);
      layer.isotherm.visible = layer.isothermInfo.count > 0;
      if (layer.isothermInfo.count) found.push(layer.isothermInfo);
    }
    isothermInfo = {
      count: found.reduce((sum, info) => sum + info.count, 0),
      typicalLow: found.length ? Math.min(...found.map((info) => info.typicalLow)) : 0,
      typicalHigh: found.length ? Math.max(...found.map((info) => info.typicalHigh)) : 0
    };
    if (found.length) {
      const { typicalLow: low, typicalHigh: high } = isothermInfo;
      for (const layer of waterLayers.values()) layer.materials.isotherm.uniforms.uDepthRange.value.set(low, low + Math.max(high - low, 5));
    }
    const target = formatTemperature(state.targetC);
    if (!isothermInfo.count) {
      const [low, high] = displayRange;
      ui.targetStatus.textContent = `No ${target} water found in the loaded data. Most water is ${formatTemperature(low)}–${formatTemperature(high)}.`;
    } else {
      ui.targetStatus.textContent = isothermInfo.typicalLow === isothermInfo.typicalHigh
        ? `${target} water is mostly about ${formatDepth(isothermInfo.typicalLow)} down.`
        : `${target} water is mostly ${formatDepth(isothermInfo.typicalLow)}–${formatDepth(isothermInfo.typicalHigh)} down.`;
    }
  }

  function defaultSection(slug) {
    const lake = terrain.find((item) => item.slug === slug);
    const [[south, west], [north, east]] = lake.bounds;
    const centre = [(south + north) / 2, (west + east) / 2];
    const wide = distanceKm([centre[0], west], [centre[0], east]) >= distanceKm([south, centre[1]], [north, centre[1]]);
    const line = wide ? [[centre[0], west], [centre[0], east]] : [[south, centre[1]], [north, centre[1]]];
    return lakeCrossing(field, LAKE_IDS[slug], ...line) || line;
  }

  function sameSection(section, other) {
    const close = (first, second) => distanceKm(first, second) < 0.03;
    return (close(section[0], other[0]) && close(section[1], other[1]))
      || (close(section[0], other[1]) && close(section[1], other[0]));
  }

  function crossedLakes(start, end) {
    return LAKES.filter((slug) => lakeCrossing(field, LAKE_IDS[slug], start, end));
  }

  function hasSection() {
    return [...waterLayers.values()].some((layer) => layer.curtain);
  }

  function updateSection() {
    const crossed = state.layers.has("section") && state.section && field ? crossedLakes(...state.section) : [];
    for (const [slug, layer] of waterLayers) {
      layer.setSection(crossed.includes(slug) ? state.section : null, terrain.find((lake) => lake.slug === slug).maxDepth);
    }
    if (shared) shared.uCutLakes.value = [...waterLayers].reduce((mask, [slug, layer]) => mask | (layer.curtain ? 1 << LAKE_IDS[slug] : 0), 0);
    const show = hasSection();
    ui.sectionControls.hidden = !state.layers.has("section");
    ui.sectionCard.hidden = !show;
    placeLegend();
    sectionCard.place();
    if (meshes.floor) {
      depthGroup.remove(meshes.floor);
      meshes.floor.geometry.dispose();
      meshes.floor.material.dispose();
      meshes.floor = null;
    }
    if (!show) {
      if (!pick) pins.forEach((pin) => { pin.visible = false; });
      return;
    }
    const [start, end] = state.section;
    meshes.floor = sectionFloorLine(field, start, end);
    depthGroup.add(meshes.floor);
    pins.forEach((pin, index) => {
      const [x, z] = toWorld(...state.section[index]);
      pin.position.set(x, 0, z);
      pin.visible = true;
    });
    drawSectionChart();
  }

  function drawSectionChart() {
    if (!waterLayers.size || !state.section || ui.sectionCard.hidden) {
      sectionColorRange = null;
      return;
    }
    const result = drawSection(ui.sectionChart, {
      volumes: new Map([...waterLayers].map(([slug, layer]) => [LAKE_IDS[slug], layer.volume])),
      field, lakeId: null, start: state.section[0], end: state.section[1],
      range: displayRange, targetC: state.layers.has("target") ? state.targetC : NaN,
      sliceDepth: state.layers.has("slice") && state.depth > 0 ? state.depth : NaN,
      dynamicScale: state.sectionDynamicScale
    });
    sectionColorRange = result?.colorRange || null;
    ui.sectionCaption.textContent = result
      ? `${formatDistance(result.lengthKm)} · deepest ${formatDepth(result.maxDepth)}${state.layers.has("target") ? ` · white line is ${formatTemperature(state.targetC)}` : ""}`
      : "";
    updateLegend();
  }

  // ---------- Depth ----------

  function maxSliceDepth() {
    return terrain.length ? Math.floor(Math.max(...terrain.map((lake) => lake.maxDepth))) : 410;
  }

  // The slider is quadratic so the shallow water anglers fish gets most of its travel.
  function depthFromSlider(value) {
    const slider = clamp(Number(value) || 0, 0, DEPTH_SLIDER_STEPS);
    return Math.round(maxSliceDepth() * (slider / DEPTH_SLIDER_STEPS) ** 2 * 2) / 2;
  }

  function normalizeDepth(value) {
    const depth = Number(value);
    return Number.isFinite(depth) ? clamp(depth, 0, maxSliceDepth()) : 0;
  }

  function sliderFromDepth(depth) {
    return Math.round(Math.sqrt(normalizeDepth(depth) / maxSliceDepth()) * DEPTH_SLIDER_STEPS);
  }

  function updateDepthRange() {
    state.depth = normalizeDepth(state.depth);
    ui.depth.min = "0";
    ui.depth.max = String(DEPTH_SLIDER_STEPS);
    ui.depth.value = String(sliderFromDepth(state.depth));
    updateDepthLabel();
  }

  function updateDepthLabel() {
    state.depth = normalizeDepth(state.depth);
    const text = state.depth <= 0 ? "Surface" : formatDepth(state.depth);
    ui.depthLabel.textContent = text;
    ui.depth.setAttribute("aria-valuetext", text);
  }

  // ---------- Controls ----------

  function bindControls() {
    ui.forecast.addEventListener("change", () => {
      state.forecast = FORECASTS.includes(Number(ui.forecast.value)) ? Number(ui.forecast.value) : 0;
      stats.interaction("forecast_changed", String(state.forecast));
      syncUrl();
      void loadVolumes();
    });
    ui.depth.addEventListener("input", () => {
      state.depth = depthFromSlider(ui.depth.value);
      stats.settle("depth_changed", depthBucket(state.depth));
      updateDepthLabel();
      updateWaterLayers();
      refreshReadout();
      scheduleUrl();
    });
    for (const [name, input] of Object.entries(ui.layerToggles)) {
      input.addEventListener("change", () => {
        if (input.checked) state.layers.add(name);
        else state.layers.delete(name);
        stats.interaction("layer_toggled", `${name}:${input.checked ? "on" : "off"}`);
        if (name === "section" && !input.checked) cancelPick();
        updateWaterLayers();
        syncUrl();
        if (name === "section" && input.checked) startPick();
      });
    }
    ui.particleDensity.addEventListener("change", () => {
      const density = Number(ui.particleDensity.value);
      state.particleDensity = PARTICLE_DENSITIES.includes(density) ? density : 1;
      updateWaterLayers();
      syncUrl();
    });
    ui.particleColor.addEventListener("change", () => {
      state.particleColor = PARTICLE_COLORS.includes(ui.particleColor.value) ? ui.particleColor.value : "speed";
      updateWaterLayers();
      syncUrl();
    });
    ui.sectionClose.addEventListener("click", () => {
      ui.layerToggles.section.checked = false;
      ui.layerToggles.section.dispatchEvent(new Event("change"));
    });
    ui.target.addEventListener("input", () => {
      state.targetC = convertUnitValue(Number(ui.target.value), unitPreference("waterTemperature"), "C");
      stats.settle("target_changed", targetBucket(state.targetC));
      updateTargetLabel();
      updateIsotherm();
      updateSceneOptions();
      updateLegend();
      drawSectionChart();
      scheduleUrl();
    });
    ui.sectionDynamicScale.addEventListener("change", () => {
      state.sectionDynamicScale = ui.sectionDynamicScale.checked;
      drawSectionChart();
      syncUrl();
    });
    ui.fitMapRange.addEventListener("change", () => {
      state.fitMapRange = ui.fitMapRange.checked;
      syncFitMapRangeControl();
      if (state.fitMapRange) fitVisibleSliceRange();
      else {
        clearTimeout(visibleSliceRangeTimer);
        visibleSliceRangeTimer = null;
        visibleSliceRange = null;
        applyMapTemperatureRange();
      }
      syncUrl();
    });
    ui.exaggeration.addEventListener("input", () => {
      setExaggeration(Number(ui.exaggeration.value) * DEFAULT_EXAGGERATION);
      stats.settle("exaggeration_changed", exaggerationBucket(state.exaggeration));
      scheduleUrl();
    });
    ui.contours.addEventListener("change", () => {
      state.contours = ui.contours.checked;
      stats.interaction("contours_toggled", state.contours ? "on" : "off");
      updateSceneOptions();
      syncUrl();
    });
    ui.bedPalette.addEventListener("change", () => {
      state.bedPalette = BED_PALETTE_NAMES.includes(ui.bedPalette.value) ? ui.bedPalette.value : "lake-floor";
      updateSceneOptions();
      requestRender();
      syncUrl();
    });
    document.addEventListener("glc-units-changed", () => {
      syncTargetSlider();
      updateDepthLabel();
      updateSceneOptions();
      updateIsotherm();
      updateLegend();
      drawSectionChart();
      refreshReadout();
    });
    ui.reset.addEventListener("click", () => {
      stats.interaction("camera_reset");
      state.camera = null;
      flyTo(cameraViewFor(state.lake), true);
    });
    ui.share.addEventListener("click", shareView);
    ui.canvas.addEventListener("keydown", handleKeydown);
    ui.canvas.addEventListener("pointermove", (event) => { pointer = event; scheduleHover(); });
    ui.canvas.addEventListener("pointerleave", () => { pointer = null; });
    // The card stays while the pointer is anywhere over the stage, so it can be reached and dragged.
    // A finger has no hover, so after a touch the last reading stays until the lake changes.
    ui.stage.addEventListener("pointerleave", (event) => { if (!pick && event.pointerType !== "touch") hideReadout(); });
    ui.canvas.addEventListener("pointerdown", (event) => {
      pointerDown = [event.clientX, event.clientY];
      ui.stage.classList.add("is-engaged");
      if (event.pointerType === "touch") hideReadout();
    });
    // Stop the browser's middle-click autoscroll so middle-drag can pan.
    ui.canvas.addEventListener("mousedown", (event) => { if (event.button === 1) event.preventDefault(); });
    ui.canvas.addEventListener("wheel", () => {
      ui.stage.classList.add("is-engaged");
      stats.camera("camera_zoom");
    }, { passive: true });
    ui.canvas.addEventListener("pointerup", handleClick);
    ui.canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      stats.issue("context_lost");
      setStatus("Graphics context lost — reload to restart", "error");
    });
    REDUCED_MOTION.addEventListener?.("change", requestRender);
  }

  function syncControls() {
    ui.reset.disabled = !field;
    ui.forecast.value = String(state.forecast);
    for (const [name, input] of Object.entries(ui.layerToggles)) input.checked = state.layers.has(name);
    ui.currentControls.hidden = !state.layers.has("currents");
    ui.particleDensity.value = String(state.particleDensity);
    ui.particleColor.value = state.particleColor;
    ui.targetControls.hidden = !state.layers.has("target");
    ui.sectionControls.hidden = !state.layers.has("section");
    ui.sectionDynamicScale.checked = state.sectionDynamicScale;
    syncFitMapRangeControl();
    if (Number.isFinite(state.exaggeration)) {
      ui.exaggeration.value = String(state.exaggeration / DEFAULT_EXAGGERATION);
      ui.exaggerationLabel.textContent = exaggerationScaleLabel(state.exaggeration);
      ui.exaggeration.setAttribute("aria-valuetext", exaggerationScaleLabel(state.exaggeration));
    }
    ui.contours.checked = state.contours;
    ui.bedPalette.value = state.bedPalette;
    ui.depth.value = String(sliderFromDepth(state.depth));
    updateDepthLabel();
    syncTargetSlider();
    updateMapLink();
  }

  function syncTargetSlider() {
    const unit = unitPreference("waterTemperature");
    if (!targetWaterBounds) {
      ui.target.disabled = true;
      updateTargetLabel();
      return false;
    }
    const step = unit === "F" ? 1 : 0.5;
    const rawMinimum = convertUnitValue(targetWaterBounds[0], "C", unit);
    const rawMaximum = convertUnitValue(targetWaterBounds[1], "C", unit);
    let minimum = Math.ceil((rawMinimum - 1e-8) / step) * step;
    let maximum = Math.floor((rawMaximum + 1e-8) / step) * step;
    let sliderStep = String(step);
    if (maximum - minimum < step) {
      minimum = rawMinimum;
      maximum = rawMaximum;
      sliderStep = "any";
    }
    Object.assign(ui.target, { min: String(minimum), max: String(maximum), step: sliderStep });
    const ready = waterLayers.size > 0 && volumeLoader.pending.size === 0 && !volumeLoader.loading;
    ui.target.disabled = !ready;
    if (!ready) {
      updateTargetLabel();
      return false;
    }
    const currentValue = convertUnitValue(state.targetC, "C", unit);
    const boundedValue = sliderStep === "any"
      ? clamp(currentValue, minimum, maximum)
      : clamp(Math.round(currentValue / step) * step, minimum, maximum);
    const nextTargetC = convertUnitValue(boundedValue, unit, "C");
    const changed = Math.abs(nextTargetC - state.targetC) > 1e-6;
    state.targetC = nextTargetC;
    ui.target.value = String(boundedValue);
    updateTargetLabel();
    if (changed) scheduleUrl();
    return changed;
  }

  function syncFitMapRangeControl() {
    ui.fitMapRange.checked = state.fitMapRange;
    ui.fitMapRange.disabled = !waterLayers.size || !state.layers.has("slice");
  }

  function updateTargetLabel() {
    ui.targetLabel.textContent = formatTemperature(state.targetC);
  }

  function handleKeydown(event) {
    if (event.key === "Escape" && pick) {
      cancelPick();
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      stats.interaction("camera_reset");
      flyTo(cameraViewFor(state.lake), true);
      return;
    }
    if (!ORBIT_KEYS.has(event.key)) return;
    stats.camera("camera_keys");
    placePivot();
    const offset = camera.position.clone().sub(orbit.target);
    const spherical = new THREE.Spherical().setFromVector3(offset);
    const step = Math.PI / 36;
    switch (event.key) {
      case "ArrowLeft": spherical.theta -= step; break;
      case "ArrowRight": spherical.theta += step; break;
      case "ArrowUp": spherical.phi = Math.max(0.05, spherical.phi - step); break;
      case "ArrowDown": spherical.phi = Math.min(orbit.maxPolarAngle, spherical.phi + step); break;
      case "+": case "=": spherical.radius = Math.max(orbit.minDistance, spherical.radius * 0.88); break;
      case "-": case "_": spherical.radius = Math.min(orbit.maxDistance, spherical.radius * 1.12); break;
    }
    event.preventDefault();
    flight = null;
    camera.position.setFromSpherical(spherical).add(orbit.target);
    orbit.update();
  }

  // ---------- Pointer: hover readout and cross-section drawing ----------

  // Where the pointer meets the visible depth slice, or else the water surface. Under
  // water the lakebed counts too, and anything behind it does not.
  function pointerLocation(event) {
    if (!field) return null;
    const rect = ui.canvas.getBoundingClientRect();
    ndc.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
    raycaster.setFromCamera(ndc, camera);
    const { origin, direction } = raycaster.ray;
    const ground = camera.position.y < 0
      ? terrainDistance(terrain, origin, direction, { exaggeration: state.exaggeration, limit: orbit.maxDistance })
      : null;
    const planes = [];
    if (waterLayers.size && state.layers.has("slice") && state.depth > 0) planes.push(state.depth);
    planes.push(0);
    for (const depth of planes) {
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), (depth / 1000) * state.exaggeration);
      if (!raycaster.ray.intersectPlane(plane, hit)) continue;
      if (ground !== null && origin.distanceTo(hit) > ground) continue;
      const [latitude, longitude] = fromWorld(hit.x, hit.z);
      const bed = bedDepthAt(field, latitude, longitude);
      if (depth > 0 && ![...waterLayers.keys()].some((slug) => LAKE_IDS[slug] === lakeIdAt(field, latitude, longitude))) continue;
      if (bed > depth || depth === 0) return { latitude, longitude, bed, onSlice: depth > 0 && bed > depth };
    }
    if (ground === null) return null;
    hit.copy(origin).addScaledVector(direction, ground);
    const [latitude, longitude] = fromWorld(hit.x, hit.z);
    return { latitude, longitude, bed: bedDepthAt(field, latitude, longitude), onSlice: false };
  }

  function scheduleHover() {
    if (hoverScheduled) return;
    hoverScheduled = true;
    requestAnimationFrame(() => {
      hoverScheduled = false;
      refreshReadout();
    });
  }

  function refreshReadout() {
    if (!pointer || flight) return;
    const location = pointerLocation(pointer);
    // Off the water the card keeps its last reading, so the pointer can travel to it and drag it.
    if (location && location.bed > 0) showReadout(location);
  }

  function showReadout({ latitude, longitude, bed }) {
    const lakeId = lakeIdAt(field, latitude, longitude);
    const slug = LAKES.find((item) => LAKE_IDS[item] === lakeId);
    const [x, z] = toWorld(latitude, longitude);
    const positions = probeLine.geometry.attributes.position;
    positions.setXYZ(0, x, 0, z);
    positions.setXYZ(1, x, -bed / 1000, z);
    positions.needsUpdate = true;
    probeLine.geometry.computeBoundingSphere();
    probeLine.visible = true;
    // Each row is [label, value, spans both columns]. The current's wording is the longest.
    const rows = [["Lakebed", formatDepth(bed)]];
    let note = "";
    const layer = waterLayers.get(slug);
    const volume = layer?.volume;
    let profile = [];
    if (volume) {
      profile = temperatureProfile(volume, latitude, longitude, bed);
      const surface = profile[0]?.[1];
      const bottom = profile[profile.length - 1]?.[1];
      if (Number.isFinite(surface)) rows.push(["Surface", formatTemperature(surface)]);
      if (state.depth > 0 && state.depth < bed) {
        rows.push([`At ${formatDepth(state.depth)}`, formatTemperature(temperatureAt(volume, latitude, longitude, state.depth))]);
      }
      if (Number.isFinite(bottom)) rows.push(["Bottom", formatTemperature(bottom)]);
      if (layer.currentField && state.layers.has("currents")) {
        const flow = currentAt(layer.currentField, latitude, longitude);
        if (flow) rows.push(["Current", `${formatUnitValue(Math.hypot(...flow) * 3.6, "speed", "kph", { decimals: 1 })} ${compass(flow)}`, true]);
      }
    } else if (slug) {
      note = volumeLoader.failed.has(slug) ? "Water data is unavailable for this lake."
        : "Water data is loading…";
    }
    ui.readoutTitle.textContent = slug ? LAKE_NAMES[slug] : "Open water";
    stats.once("readout_viewed", ui.readoutTitle.textContent);
    ui.readoutValues.replaceChildren(...rows.map(([term, value, wide]) => {
      const row = document.createElement("div");
      row.classList.toggle("is-wide", Boolean(wide));
      const dt = document.createElement("dt");
      dt.textContent = term;
      const dd = document.createElement("dd");
      dd.textContent = value;
      row.append(dt, dd);
      return row;
    }));
    ui.readoutNote.textContent = note;
    ui.readoutNote.hidden = !note;
    ui.readout.hidden = false;
    readoutCard.place();
    ui.readoutProfile.hidden = profile.length < 2;
    if (profile.length >= 2) {
      latestReadoutProfile = {
        points: profile, range: displayRange, sliceDepth: state.depth > 0 ? state.depth : NaN,
        targetC: state.layers.has("target") ? state.targetC : NaN
      };
      drawProfile(ui.readoutProfile, latestReadoutProfile);
    } else {
      latestReadoutProfile = null;
    }
    requestRender();
  }

  function hideReadout() {
    ui.readout.hidden = true;
    if (probeLine.visible) {
      probeLine.visible = false;
      requestRender();
    }
  }

  function startPick() {
    if (!field) return;
    state.section = null;
    state.sectionDrawn = false;
    pick = { points: [] };
    ui.canvas.classList.add("is-picking");
    ui.pickHint.hidden = false;
    ui.pickHint.textContent = "Click where the cross-section should start, on the water or the shore. Press Esc to cancel.";
    pins.forEach((pin) => { pin.visible = false; });
    updateSection();
    syncUrl();
    requestRender();
  }

  function cancelPick() {
    if (!pick) return;
    pick = null;
    ui.canvas.classList.remove("is-picking");
    ui.pickHint.hidden = true;
    updateSection();
    requestRender();
  }

  function handleClick(event) {
    const moved = pointerDown ? Math.hypot(event.clientX - pointerDown[0], event.clientY - pointerDown[1]) : 0;
    pointerDown = null;
    if (moved > 5) {
      // Dragging a button other than the left, or with a modifier held, pans; a plain left drag orbits.
      const panning = event.button !== 0 || event.shiftKey || event.ctrlKey || event.metaKey;
      stats.camera(panning ? "camera_pan" : "camera_orbit");
    }
    if (moved > 5 || event.button !== 0) return;
    const location = pointerLocation(event);
    if (!pick) {
      if (state.layers.has("section")) startPick();
      else if (location?.bed > 0) {
        const slug = LAKES.find((item) => LAKE_IDS[item] === lakeIdAt(field, location.latitude, location.longitude));
        if (slug && slug !== state.lake) selectLake(slug, "scene");
      }
      if (!pick) return;
    }
    // Ends can sit on land, so a line can run shore to shore.
    if (!location || !insideGrid(field, location.latitude, location.longitude)) {
      ui.pickHint.textContent = "Click on the map, on the water or the shore.";
      return;
    }
    const point = [location.latitude, location.longitude];
    if (pick.points.length === 1 && distanceKm(pick.points[0], point) >= 1 && !crossedLakes(pick.points[0], point).length) {
      ui.pickHint.textContent = "That line does not cross a lake. Click somewhere else for the end.";
      return;
    }
    pick.points.push(point);
    const pin = pins[pick.points.length - 1];
    const [x, z] = toWorld(location.latitude, location.longitude);
    pin.position.set(x, 0, z);
    pin.visible = true;
    requestRender();
    if (pick.points.length === 1) {
      ui.pickHint.textContent = "Now click where it should end.";
      return;
    }
    const [start, end] = pick.points;
    pick = null;
    cancelPickUi();
    if (distanceKm(start, end) < 1) return updateSection();
    state.section = [start, end];
    state.sectionDrawn = true;
    stats.interaction("section_drawn");
    updateSection();
    syncUrl();
    focusSection();
  }

  function cancelPickUi() {
    ui.canvas.classList.remove("is-picking");
    ui.pickHint.hidden = true;
  }

  // ---------- Rendering ----------

  function requestRender() { needsRender = true; }

  // Keep the cut-away on the camera's side of the cross-section line.
  function updateCut() {
    const cut = shared?.uCut.value;
    if (!cut) return;
    if (!hasSection() || !state.section) {
      cut.w = 0;
      for (const layer of waterLayers.values()) layer.particles.setClippingPlanes([]);
      return;
    }
    const [x0, z0] = toWorld(...state.section[0]);
    const [x1, z1] = toWorld(...state.section[1]);
    // A near-vertical (north-south) line is nudged so its slope stays finite.
    const dx = Math.abs(x1 - x0) < 1e-3 ? 1e-3 : x1 - x0;
    const slope = (z1 - z0) / dx;
    const side = (camera.position.z - z0) - slope * (camera.position.x - x0);
    // Drop the half where (z - z0) - slope (x - x0) has the camera's sign.
    cut.set(x0, z0, slope, side > 0 ? 2 : 1);
    // three.js keeps points where normal . p + constant >= 0, so point the
    // normal away from the camera's side.
    const normal = new THREE.Vector3(slope, 0, -1).multiplyScalar(side > 0 ? 1 : -1).normalize();
    particleCut.setFromNormalAndCoplanarPoint(normal, new THREE.Vector3(x0, 0, z0));
    for (const layer of waterLayers.values()) layer.particles.setClippingPlanes(layer.curtain ? [particleCut] : []);
  }

  function frame(now) {
    requestAnimationFrame(frame);
    const elapsed = now - lastFrame;
    const dt = Math.min(0.05, elapsed / 1000);
    lastFrame = now;
    let changed = stepFlight(now);
    if (stepViewOffset(dt)) changed = true;
    updatePolarLimit();
    if (orbit.update()) changed = true;
    if (keepAboveFloor()) changed = true;
    const flowing = [...waterLayers.values()].filter((layer) => layer.particles.object.visible);
    if (flowing.length && !REDUCED_MOTION.matches && !document.hidden) {
      for (const layer of flowing) layer.particles.step(dt);
      changed = true;
    }
    if (!changed && !needsRender) return;
    needsRender = false;
    // Keep depth precision where the camera is looking, so the shallow
    // lakebed never flickers through the water surface, but pull the near
    // plane in when the camera is close to the surface or the lakebed so
    // neither is cut away around it.
    const height = updateUnderwater();
    const distance = camera.position.distanceTo(orbit.target);
    const closest = Math.min(Math.abs(camera.position.y), height);
    camera.near = Math.max(0.02, Math.min(distance * 0.02, closest * 0.4));
    camera.far = distance * 12 + 2000;
    camera.updateProjectionMatrix();
    updateCut();
    renderer.render(scene, camera);
  }

  function resize() {
    const { width, height } = ui.canvas.getBoundingClientRect();
    if (!width || !height) return;
    renderer.setSize(width, height, false);
    updateParticlePixelRatio();
    viewSize = { width, height };
    applyViewOffset();
    placeCards();
    requestRender();
    if (state.fitMapRange) scheduleVisibleSliceRangeFit();
    scheduleParticleViewUpdate();
  }

  function updateParticlePixelRatio() {
    const pixelRatio = renderer.getPixelRatio();
    for (const layer of waterLayers.values()) layer.particles.setPixelRatio(pixelRatio);
  }

  // The free part of the stage changes with the window and as the panel opens or closes.
  function placeCards() {
    if (ui.sectionChart.clientWidth !== chartWidth) {
      chartWidth = ui.sectionChart.clientWidth;
      drawSectionChart();
    }
    sectionCard.place();
    readoutCard.place();
  }

  // The camera looks at the middle of the free part of the stage rather than the middle of the
  // window: the projection is shifted right by half the panel's width (or up by half the sheet's
  // height). Picking and the lake labels use the same projection, so they follow.
  function applyViewOffset() {
    const { width, height } = viewSize;
    if (width && height) camera.setViewOffset(width, height, -viewShift.left / 2, viewShift.bottom / 2, width, height);
  }

  function stepViewOffset(dt) {
    const ease = REDUCED_MOTION.matches ? 1 : 1 - Math.exp(-dt * 12);
    let moved = false;
    for (const side of ["left", "bottom"]) {
      const gap = panel.insets[side] - viewShift[side];
      if (!gap) continue;
      viewShift[side] = Math.abs(gap) < 0.5 ? panel.insets[side] : viewShift[side] + gap * ease;
      moved = true;
    }
    if (moved) {
      applyViewOffset();
      if (state.fitMapRange) scheduleVisibleSliceRangeFit();
    }
    return moved;
  }

  // ---------- Cross-section card: legend inside ----------

  // The legend lives inside the cross-section card while one is showing, and bottom-left otherwise.
  function placeLegend() {
    const home = ui.sectionCard.hidden ? ui.dock : ui.legendSlot;
    if (ui.legend.parentElement !== home) home.append(ui.legend);
  }

  // ---------- Legend, status, formatting ----------

  function updateLegend() {
    const showTemperature = waterLayers.size > 0 && (state.layers.has("slice") || hasSection());
    const showTarget = waterLayers.size > 0 && state.layers.has("target") && Boolean(isothermInfo?.count);
    ui.legend.hidden = !showTemperature && !showTarget;
    ui.legendTemperature.hidden = !showTemperature;
    ui.legendTarget.hidden = !showTarget;
    if (showTemperature) {
      const temperatureRange = state.sectionDynamicScale && hasSection() && sectionColorRange
        ? sectionColorRange
        : state.layers.has("slice") ? normalMapTemperatureRange() : displayRange;
      const [low, high] = temperatureRange;
      ui.legendBar.style.background = `linear-gradient(90deg, ${TEMPERATURE_STOPS.map(([stop, color]) => `rgb(${color.join(",")}) ${(stop * 100).toFixed(1)}%`).join(", ")})`;
      ui.legendTicks.replaceChildren(...[low, (low + high) / 2, high].map((value) => legendTick(formatTemperature(value))));
    }
    if (showTarget) {
      // Mirrors the isotherm shader: pale where the water is shallow, dark where it is deep.
      const uniforms = waterLayers.values().next().value.materials.isotherm.uniforms;
      const { r, g, b } = uniforms.uTargetColor.value;
      const scale = (factors) => `rgb(${[r, g, b].map((channel, index) => Math.round(channel * factors[index] * 255)).join(",")})`;
      const pale = `rgb(${[r, g, b].map((channel) => Math.round((channel + (1 - channel) * 0.55) * 255)).join(",")})`;
      ui.legendTargetBar.style.background = `linear-gradient(90deg, ${pale}, ${scale([1, 1, 1])}, ${scale([0.26, 0.3, 0.42])})`;
      ui.legendTargetTitle.textContent = `Depth of ${formatTemperature(state.targetC)} water`;
      const [shallow, deep] = uniforms.uDepthRange.value.toArray();
      ui.legendTargetTicks.replaceChildren(legendTick(formatDepth(shallow)), legendTick(`${formatDepth(deep)}+`));
    }
    sectionCard.place();
  }

  function legendTick(text) {
    const span = document.createElement("span");
    span.textContent = text;
    return span;
  }

  function setStatus(message, kind) {
    ui.status.textContent = message;
    ui.status.dataset.state = kind;
  }

  function showDataStatus(message) {
    ui.dataStatus.hidden = false;
    ui.dataStatus.textContent = message;
  }

  function hideDataStatus() {
    ui.dataStatus.hidden = true;
    ui.dataStatus.textContent = "";
  }

  function formatDepth(metres) {
    return formatUnitValue(metres, "depth", "m", { decimals: 0 });
  }

  function exaggerationScaleLabel(value) {
    return `${Number((value / DEFAULT_EXAGGERATION).toFixed(2))}×`;
  }

  function formatTemperature(celsius) {
    return formatUnitValue(celsius, "waterTemperature", "C", { decimals: unitPreference("waterTemperature") === "C" ? 1 : 0 });
  }

  function formatDistance(km) {
    return formatUnitValue(km * 1000, "distance", "m", { decimals: 0 });
  }

  function compass([east, north]) {
    const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    const bearing = (Math.atan2(east, north) * 180 / Math.PI + 360) % 360;
    return `toward ${directions[Math.round(bearing / 45) % 8]}`;
  }

  // ---------- URL state ----------

  function readUrlState() {
    const params = new URLSearchParams(location.search);
    const lake = params.get("lake");
    const mode = params.get("mode");
    const forecast = Number(params.get("forecast"));
    const depth = Number(params.get("depth"));
    const exaggeration = Number(params.get("exaggeration"));
    const target = Number(params.get("target"));
    const bedPalette = params.get("bedPalette");
    const particleDensity = Number(params.get("particleDensity"));
    const particleColor = params.get("particleColor");
    const layers = params.has("layers")
      ? new Set(params.get("layers").split(",").filter((name) => LAYERS.includes(name)))
      : new Set(DEFAULT_LAYERS);
    const section = (params.get("section") || "").split(",").map(Number);
    return {
      // Older links used mode=bottom for the all-lakes view.
      lake: LAKES.includes(lake) && mode !== "bottom" ? lake : null,
      forecast: FORECASTS.includes(forecast) ? forecast : 0,
      depth: Number.isFinite(depth) ? clamp(depth, 0, 410) : 0,
      exaggeration: params.has("exaggeration") && Number.isFinite(exaggeration) ? clamp(Math.round(exaggeration), MIN_EXAGGERATION, MAX_EXAGGERATION) : null,
      targetC: params.has("target") && Number.isFinite(target) ? clamp(target, 0, 30) : DEFAULT_TARGET_C,
      bedPalette: BED_PALETTE_NAMES.includes(bedPalette) ? bedPalette : "lake-floor",
      particleDensity: PARTICLE_DENSITIES.includes(particleDensity) ? particleDensity : 1,
      particleColor: PARTICLE_COLORS.includes(particleColor) ? particleColor : "speed",
      layers,
      section: section.length === 4 && section.every(Number.isFinite) ? [[section[0], section[1]], [section[2], section[3]]] : null,
      sectionDrawn: params.get("sectionDrawn") === "1",
      sectionDynamicScale: params.get("sectionScale") !== "global",
      fitMapRange: params.get("fitRange") === "1",
      contours: params.get("contours") !== "0",
      camera: readVector(params.get("camera")),
      look: readVector(params.get("look"))
    };
  }

  function scheduleUrl() {
    clearTimeout(urlTimer);
    urlTimer = setTimeout(syncUrl, 250);
  }

  function syncUrl() {
    const params = new URLSearchParams();
    if (state.lake) params.set("lake", state.lake);
    if (state.forecast) params.set("forecast", String(state.forecast));
    if (state.depth) params.set("depth", String(state.depth));
    if (state.exaggeration !== DEFAULT_EXAGGERATION) params.set("exaggeration", String(state.exaggeration));
    if (state.bedPalette !== "lake-floor") params.set("bedPalette", state.bedPalette);
    if (state.particleDensity !== 1) params.set("particleDensity", String(state.particleDensity));
    if (state.particleColor !== "speed") params.set("particleColor", state.particleColor);
    const layers = LAYERS.filter((name) => state.layers.has(name));
    if (layers.join(",") !== [...DEFAULT_LAYERS].join(",")) params.set("layers", layers.join(","));
    if (state.layers.has("target") || Math.abs(state.targetC - DEFAULT_TARGET_C) > 1e-6) params.set("target", String(Number(state.targetC.toFixed(2))));
    if (state.section && state.layers.has("section")) {
      params.set("section", state.section.flat().map((value) => value.toFixed(4)).join(","));
      if (state.sectionDrawn) params.set("sectionDrawn", "1");
    }
    if (!state.sectionDynamicScale) params.set("sectionScale", "global");
    if (state.fitMapRange) params.set("fitRange", "1");
    if (!state.contours) params.set("contours", "0");
    params.set("camera", camera.position.toArray().map((value) => value.toFixed(2)).join(","));
    params.set("look", orbit.target.toArray().map((value) => value.toFixed(2)).join(","));
    history.replaceState(null, "", `${location.pathname}?${params}`);
    updateMapLink();
  }

  function updateMapLink() {
    if (!ui.returnMap) return;
    if (!state.lake) {
      ui.returnMap.href = "/map";
      return;
    }
    const [latitude, longitude, zoom] = LAKE_MAP_VIEWS[state.lake];
    ui.returnMap.href = `/map#map=${zoom}/${latitude.toFixed(4)}/${longitude.toFixed(4)}&layer=temperature&forecast=${state.forecast}&depth=${Math.round(state.depth)}`;
  }

  function shareView() {
    syncUrl();
    const copied = () => {
      stats.interaction("view_shared", "copied");
      ui.shareFeedback.textContent = "Link to this view copied.";
      ui.share.textContent = "Link copied";
      setTimeout(() => { ui.shareFeedback.textContent = ""; ui.share.textContent = "Share this view"; }, 2200);
    };
    const manual = () => {
      stats.interaction("view_shared", "manual");
      ui.shareFeedback.textContent = "Copy the address from your browser to share this view.";
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(location.href).then(copied).catch(manual);
    else manual();
  }

  function showFallback() {
    stats.issue("webgl_unavailable");
    ui.fallback.hidden = false;
    setStatus("3D graphics are unavailable", "error");
    root.querySelectorAll("[data-panel-body] :is(button, select, input), .three-d-scene-topline button").forEach((control) => { control.disabled = true; });
  }
}

function readVector(value) {
  if (!value) return null;
  const values = value.split(",").map(Number);
  return values.length === 3 && values.every((number) => Number.isFinite(number) && Math.abs(number) < 20000) ? values : null;
}

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}
